import React, { Fragment, useRef, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../Services/translation';
import { toggle } from '../Actions/Cart.action';
import { update, loadError } from '../Actions/Cart.action';
import { reloadPayment } from '../Actions/Checkout.action';
import { catchError } from '../Actions/Error.action';
import * as debounce from 'lodash.debounce';
import uuid from '../Haki/Utils/UUID';

const mapCartToAbort = {};

const quantityDispatch = debounce((articleNumber, quantity, dispatch) => {
    mapCartToAbort[articleNumber] = new AbortController();
    dispatch(update(articleNumber, quantity, mapCartToAbort[articleNumber]))
        .then(() => dispatch(reloadPayment()))
        .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
}, 500);

const MiniCart = () => {
    const dispatch = useDispatch();
    const cart = useSelector((state) => state.cart);
    const { discountRows = [], productTotal, quantity, showInfo, checkoutUrl } = cart;
    const [orderRows, setOrderRows] = useState(cart?.orderRows || []);
    const toggleRef = useRef(null);

    useEffect(() => {
        setOrderRows(cart?.orderRows || []);
    }, [cart]);

    const onToggle = () => dispatch(toggle());

    const removeOrderRow = useCallback(
        (articleNumber) =>
            dispatch(update(articleNumber, 0))
                .then(() => dispatch(reloadPayment()))
                .catch((ex) => dispatch(catchError(ex, (error) => loadError(error)))),
        [dispatch]
    );

    const handleQuantityInput = useCallback(
        (articleNumber, quantityValue, rowSystemId, ensureCorrectness = true) => {
            let floatQuantity = parseFloat(quantityValue);
            let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
            if (ensureCorrectness) {
                quantityValue = validValue ? floatQuantity : 1;
                floatQuantity = quantityValue;
                validValue = true;
            }
            const index = orderRows.findIndex((item) => item.rowSystemId === rowSystemId);
            if (index < 0) {
                return;
            }
            const oldQuantity = parseFloat(orderRows[index].quantity);
            if (floatQuantity !== oldQuantity) {
                mapCartToAbort[articleNumber] && mapCartToAbort[articleNumber].abort();
                quantityDispatch && quantityDispatch.cancel();
                validValue && quantityDispatch(articleNumber, quantityValue, dispatch);
            }
            const tempOrderRows = [...orderRows];
            tempOrderRows[index] = {
                ...tempOrderRows[index],
                quantity: quantityValue,
            };
            setOrderRows(tempOrderRows);
        },
        [dispatch, orderRows]
    );
    const handleCheckoutClick = (e) => {
        e.preventDefault();
        localStorage.setItem('checkoutFromMiniCart', '1');
        window.location.href = checkoutUrl;
    };

    useEffect(() => {
        if (showInfo) {
            document.body.classList.add('scroll-locked');
        } else {
            document.body.classList.remove('scroll-locked');
        }

        if (!showInfo) return;
        const handleClick = (e) => {
            if (toggleRef.current && !toggleRef.current.contains(e.target)) {
                onToggle();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.body.classList.remove('scroll-locked');
            document.removeEventListener('mousedown', handleClick);
        };
    }, [showInfo]);

    const ProductImage = ({ order }) => (
        <img className="checkout-cart__image" src={order.image} alt={order.name} />
    );

    const ProductName = ({ order }) => (
        <Fragment>
            <a className="checkout-cart__product-name" href={order.url}>
                {order.name}
            </a>
            {order.brand && <span className="checkout-cart__brand-name">{order.brand}</span>}
        </Fragment>
    );

    const ProductTotalPrice = ({ order }) => (
        <Fragment>
            {order.totalCampaignPrice ? (
                <div className="order-row-price-column">
                    {order.bundlePrice ? (
                        <p className="original-price__inactive">{order.bundlePrice} </p>
                    ) : (
                        <p className="original-price__inactive">{order.totalPrice}</p>
                    )}
                    <p className="campaign-price">{order.totalCampaignPrice} </p>
                </div>
            ) : (
                <p className="original-price">{order.totalPrice}</p>
            )}
        </Fragment>
    );

    const RemoveBtn = useCallback(
        ({ order }) => (
            <Fragment>
                {!order.isFreeGift && (
                    <a
                        className="table__icon table__icon--delete"
                        onClick={() => removeOrderRow(order.articleNumber)}
                        title={translate('general.remove')}
                        tabIndex={0}
                        role="button"
                        aria-label={translate('general.remove')}
                    ></a>
                )}
            </Fragment>
        ),
        [removeOrderRow]
    );

    if (!checkoutUrl) {
        return null;
    }

    return (
        <div className="cart cart--mini">
            <a
                className={`cart__link--block${showInfo ? ' disabled-link' : ''}`}
                rel="nofollow"
                href="#"
                aria-label={translate('general.cart')}
                aria-expanded={showInfo}
                onClick={e => { e.preventDefault(); onToggle(); }}
            >
                <i className="cart__icon" aria-hidden="true">
                    {quantity > 0 && <span className="cart__quantity">{quantity}</span>}
                </i>
            </a>
            {showInfo && (
                <div className="minicart-modal-overlay checkout__container">
                    <div ref={toggleRef} className="minicart-modal">
                        <div className="minicart-modal__header">
                            <span className="minicart-modal__title">
                                {translate('checkout.cart.yourarticles')}
                            </span>
                            <button
                                className="minicart-modal__close"
                                onClick={onToggle}
                                aria-label={translate('general.close')}
                            >
                                <span className="table__icon table__icon--close"></span>
                            </button>
                        </div>
                        <div className="minicart-modal__body">
                            <div className="checkout-cart__item-wrapper">
                                {orderRows.length === 0 && (
                                    <div className="minicart-modal__empty">
                                        {translate('checkout.cart.empty') || ''}
                                    </div>
                                )}
                                {orderRows.map((order) => (
                                    <div
                                        key={order.rowSystemId || order.articleNumber}
                                        className="row small-unstack simple-table no-margin checkout-cart__row minicart-modal__item"
                                    >
                                        <div className="checkout-cart__info-wrapper">
                                            <div className="cartlineitem-wrapper">
                                                <div className="small-2 checkout-cart__image-container image-wrapper">
                                                    <div className="checkout-cart__image-wrapper">
                                                        <ProductImage order={order} />
                                                    </div>
                                                </div>
                                                <div className="text-wrapper small-10 ">
                                                    <div>
                                                        <div className="checkout-cart__info-container">
                                                            <div className="checkout-cart__image-info">
                                                                <ProductName order={order} />
                                                                <div className="shipment-information">
                                                                    <div className="shipment-information--stock">
                                                                        <div
                                                                            className="shipment-information__dot"
                                                                            style={{
                                                                                background: order.shipmentInformation?.colorCode,
                                                                            }}
                                                                        ></div>
                                                                        <span className="shipment-information__text-availability">
                                                                            {order.shipmentInformation?.stockAvailabilityText}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="checkout-cart__quantity-wrapper">
                                                                {order.isFreeGift ? (
                                                                    <div className="checkout-cart__input">{order.quantity}</div>
                                                                ) : (
                                                                    <input
                                                                        aria-label={translate('product.quantity')}
                                                                        className="checkout-cart__input"
                                                                        type="number"
                                                                        min="1"
                                                                        id={`minicart-quantity-${order.articleNumber}`}
                                                                        maxLength={3}
                                                                        value={order.quantity.toString()}
                                                                        onChange={(event) =>
                                                                            handleQuantityInput(
                                                                                order.articleNumber,
                                                                                event.target.value,
                                                                                order.rowSystemId,
                                                                                false
                                                                            )
                                                                        }
                                                                        onBlur={(event) =>
                                                                            handleQuantityInput(
                                                                                order.articleNumber,
                                                                                event.target.value,
                                                                                order.rowSystemId
                                                                            )
                                                                        }
                                                                    />
                                                                )}
                                                                <div className="checkout-cart__remove-item">
                                                                    <RemoveBtn order={order} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="checkout-cart__price-wrapper">
                                                        <div className="checkout-cart__total-price">
                                                            <ProductTotalPrice order={order} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {discountRows.map((order) => (
                                    <div
                                        className="row small-unstack no-margin checkout-cart__row minicart-modal__item"
                                        key={order.rowSystemId || uuid()}
                                    >
                                        <div className="columns small-12 medium-4 large-5 checkout-cart__image-container">
                                            <div className="checkout-cart__image-info">
                                                <ProductName order={order} />
                                            </div>
                                        </div>
                                        <div className="columns small-3 medium-2 large-2 simple-table__cell--no-break-word"></div>
                                        <div className="columns small-2 medium-2 large-2"></div>
                                        <div className="checkout-cart__total-price columns small-2 medium-3 large-2 simple-table__cell--no-break-word">
                                            <ProductTotalPrice order={order} />
                                        </div>
                                        <div className="columns small-3 medium-1 large-1"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="minicart-modal__footer">
                            <div className="minicart-modal__totalrow">
                                <span>{translate('checkout.cart.total')}</span>
                                <span>{productTotal}</span>
                            </div>
                            <a className="minicart-modal__checkout" href={checkoutUrl} onClick={handleCheckoutClick}>
                                {translate('checkout.cart.gotocheckout') || ''}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MiniCart;
